import React, { useState, useEffect } from 'react';
import { AdminHeader } from './AdminLayout';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
    Users, 
    Plus,
    Trash2,
    Shield,
    ShieldCheck,
    User,
    Pencil,
    Eye,
    Key,
    Search,
    UserCog
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Role display configuration
const ROLE_CONFIG = {
    super_admin: { 
        label: 'Super Admin', 
        icon: ShieldCheck, 
        color: 'text-red-600', 
        bgColor: 'bg-red-50',
        description: 'Vollzugriff auf alle Funktionen'
    },
    admin: { 
        label: 'Admin', 
        icon: Shield, 
        color: 'text-orange-600', 
        bgColor: 'bg-orange-50',
        description: 'Kann Benutzer und Daten verwalten'
    },
    verwaltung: { 
        label: 'Verwaltung', 
        icon: UserCog, 
        color: 'text-blue-600', 
        bgColor: 'bg-blue-50',
        description: 'Kann Daten verwalten, keine Einstellungen'
    },
    betrachter: { 
        label: 'Betrachter', 
        icon: Eye, 
        color: 'text-gray-600', 
        bgColor: 'bg-gray-50',
        description: 'Kann nur Daten ansehen'
    },
    koordinator: { 
        label: 'Koordinator', 
        icon: User, 
        color: 'text-teal-600', 
        bgColor: 'bg-teal-50',
        description: 'Koordinator für bestimmte Gemeinden'
    },
    lehrperson: { 
        label: 'Lehrperson', 
        icon: User, 
        color: 'text-violet-600', 
        bgColor: 'bg-violet-50',
        description: 'Lehrperson mit eigenem Bereich'
    },
};

export function EinstellungenBenutzer() {
    const [users, setUsers] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [permissions, setPermissions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Dialog states
    const [showUserDialog, setShowUserDialog] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [passwordDialog, setPasswordDialog] = useState(null);
    
    // Form data
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        vorname: '',
        nachname: '',
        role: 'verwaltung',
        aktiv: true,
    });
    const [newPassword, setNewPassword] = useState('');
    
    useEffect(() => {
        fetchData();
    }, []);
    
    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            const headers = { Authorization: `Bearer ${token}` };
            
            const [usersRes, rolesRes, permissionsRes] = await Promise.all([
                axios.get(`${API_URL}/api/users`, { headers }),
                axios.get(`${API_URL}/api/users/roles`, { headers }),
                axios.get(`${API_URL}/api/users/permissions`, { headers }),
            ]);
            
            setUsers(usersRes.data);
            // API returns {roles: [...]} so extract the array
            setAvailableRoles(rolesRes.data.roles || rolesRes.data);
            setPermissions(permissionsRes.data);
        } catch (err) {
            console.error('Failed to fetch data:', err);
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };
    
    const handleOpenDialog = (user = null) => {
        if (user) {
            setEditUser(user);
            setFormData({
                email: user.email,
                password: '',
                vorname: user.vorname || '',
                nachname: user.nachname || '',
                role: user.role,
                aktiv: user.aktiv !== false,
            });
        } else {
            setEditUser(null);
            setFormData({
                email: '',
                password: '',
                vorname: '',
                nachname: '',
                role: availableRoles[0]?.id || availableRoles[0]?.value || 'verwaltung',
                aktiv: true,
            });
        }
        setShowUserDialog(true);
    };
    
    const handleSaveUser = async () => {
        if (!formData.email) {
            toast.error('E-Mail ist erforderlich');
            return;
        }
        if (!editUser && !formData.password) {
            toast.error('Passwort ist erforderlich');
            return;
        }
        
        try {
            const token = localStorage.getItem('auth_token');
            const headers = { Authorization: `Bearer ${token}` };
            
            if (editUser) {
                // Update existing user
                await axios.put(`${API_URL}/api/users/${editUser.id}`, {
                    email: formData.email,
                    vorname: formData.vorname,
                    nachname: formData.nachname,
                    role: formData.role,
                    aktiv: formData.aktiv,
                    koordinator_gemeinden: [],
                }, { headers });
                toast.success('Benutzer aktualisiert');
            } else {
                // Create new user
                await axios.post(`${API_URL}/api/users`, {
                    email: formData.email,
                    password: formData.password,
                    vorname: formData.vorname,
                    nachname: formData.nachname,
                    role: formData.role,
                    aktiv: formData.aktiv,
                    koordinator_gemeinden: [],
                }, { headers });
                toast.success('Benutzer erstellt');
            }
            
            setShowUserDialog(false);
            fetchData();
        } catch (err) {
            console.error('Failed to save user:', err);
            const message = err.response?.data?.detail || 'Fehler beim Speichern';
            toast.error(message);
        }
    };
    
    const handleDeleteUser = async () => {
        if (!deleteConfirm) return;
        
        try {
            const token = localStorage.getItem('auth_token');
            await axios.delete(`${API_URL}/api/users/${deleteConfirm.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Benutzer gelöscht');
            setDeleteConfirm(null);
            fetchData();
        } catch (err) {
            console.error('Failed to delete user:', err);
            const message = err.response?.data?.detail || 'Fehler beim Löschen';
            toast.error(message);
        }
    };
    
    const handleChangePassword = async () => {
        if (!passwordDialog || !newPassword) {
            toast.error('Passwort ist erforderlich');
            return;
        }
        
        try {
            const token = localStorage.getItem('auth_token');
            await axios.put(
                `${API_URL}/api/users/${passwordDialog.id}/password`,
                null,
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    params: { password: newPassword }
                }
            );
            toast.success('Passwort geändert');
            setPasswordDialog(null);
            setNewPassword('');
        } catch (err) {
            console.error('Failed to change password:', err);
            const message = err.response?.data?.detail || 'Fehler beim Ändern des Passworts';
            toast.error(message);
        }
    };
    
    // Filter users by search term
    const filteredUsers = users.filter(user => {
        const search = searchTerm.toLowerCase();
        return (
            user.email?.toLowerCase().includes(search) ||
            user.vorname?.toLowerCase().includes(search) ||
            user.nachname?.toLowerCase().includes(search) ||
            ROLE_CONFIG[user.role]?.label.toLowerCase().includes(search)
        );
    });
    
    // Group users by role for display
    const groupedUsers = filteredUsers.reduce((acc, user) => {
        const role = user.role || 'other';
        if (!acc[role]) acc[role] = [];
        acc[role].push(user);
        return acc;
    }, {});
    
    // Order of roles to display
    const roleOrder = ['super_admin', 'admin', 'verwaltung', 'betrachter', 'koordinator', 'lehrperson'];
    
    if (loading) {
        return (
            <>
                <AdminHeader title="Benutzerverwaltung" />
                <div className="admin-content">
                    <div className="max-w-4xl">
                        <div className="bg-card rounded-2xl p-6 animate-pulse">
                            <div className="h-6 bg-muted rounded w-48 mb-4" />
                            <div className="space-y-4">
                                {[1,2,3].map(i => (
                                    <div key={i} className="h-16 bg-muted rounded w-full" />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
    
    // Check if user can manage users
    // permissions.permissions is an array of permission objects with id field
    const canManageUsers = permissions?.role === 'super_admin' || 
        permissions?.permissions?.some(p => p.id === 'manage_users');
    
    if (!canManageUsers) {
        return (
            <>
                <AdminHeader title="Benutzerverwaltung" />
                <div className="admin-content">
                    <div className="max-w-4xl">
                        <div className="bg-card rounded-2xl border p-8 text-center">
                            <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <h2 className="text-lg font-semibold mb-2">Keine Berechtigung</h2>
                            <p className="text-muted-foreground">
                                Sie haben keine Berechtigung, Benutzer zu verwalten.
                            </p>
                        </div>
                    </div>
                </div>
            </>
        );
    }
    
    return (
        <>
            <AdminHeader title="Benutzerverwaltung">
                <Button onClick={() => handleOpenDialog()} className="rounded-xl">
                    <Plus className="h-4 w-4 mr-2" />
                    Benutzer hinzufügen
                </Button>
            </AdminHeader>
            
            <div className="admin-content">
                <div className="max-w-4xl space-y-6">
                    {/* Search - Unified Style */}
                    <div className="flex flex-wrap items-center gap-4 mb-8">
                        <div className="search-wrapper flex-1 max-w-xl">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors" />
                            <Input
                                placeholder="Benutzer suchen..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <div className="text-base text-muted-foreground">
                            {filteredUsers.length} von {users.length} Benutzer
                        </div>
                    </div>
                    
                    {/* User list grouped by role */}
                    {roleOrder.map(role => {
                        const usersInRole = groupedUsers[role];
                        if (!usersInRole || usersInRole.length === 0) return null;
                        
                        const config = ROLE_CONFIG[role] || { 
                            label: role, 
                            icon: User, 
                            color: 'text-gray-600',
                            bgColor: 'bg-gray-50',
                            description: ''
                        };
                        const RoleIcon = config.icon;
                        
                        return (
                            <div key={role} className="bg-card rounded-2xl border overflow-hidden">
                                <div className="p-4 border-b bg-muted/30 flex items-center gap-3">
                                    <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", config.bgColor)}>
                                        <RoleIcon className={cn("h-5 w-5", config.color)} />
                                    </div>
                                    <div>
                                        <h2 className="font-semibold">{config.label}</h2>
                                        <p className="text-sm text-muted-foreground">{config.description}</p>
                                    </div>
                                    <span className="ml-auto bg-muted px-3 py-1 rounded-full text-sm font-medium">
                                        {usersInRole.length}
                                    </span>
                                </div>
                                
                                <div className="divide-y">
                                    {usersInRole.map(user => (
                                        <div 
                                            key={user.id} 
                                            className={cn(
                                                "p-4 flex items-center justify-between hover:bg-muted/30 transition-colors",
                                                !user.aktiv && "opacity-50"
                                            )}
                                            data-testid={`user-${user.id}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={cn(
                                                    "w-10 h-10 rounded-full flex items-center justify-center text-white font-medium",
                                                    user.aktiv ? "bg-primary" : "bg-gray-400"
                                                )}>
                                                    {(user.vorname?.[0] || user.email[0]).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-medium">
                                                        {user.vorname && user.nachname 
                                                            ? `${user.vorname} ${user.nachname}`
                                                            : user.email}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">{user.email}</p>
                                                    {!user.aktiv && (
                                                        <span className="text-xs text-red-500">Deaktiviert</span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setPasswordDialog(user)}
                                                    title="Passwort ändern"
                                                >
                                                    <Key className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleOpenDialog(user)}
                                                    title="Bearbeiten"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setDeleteConfirm(user)}
                                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                    title="Löschen"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    
                    {filteredUsers.length === 0 && (
                        <div className="bg-card rounded-2xl border p-8 text-center">
                            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">
                                {searchTerm ? 'Keine Benutzer gefunden' : 'Keine Benutzer vorhanden'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Create/Edit User Dialog */}
            <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl">
                            {editUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer erstellen'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Vorname</Label>
                                <Input
                                    value={formData.vorname}
                                    onChange={(e) => setFormData({...formData, vorname: e.target.value})}
                                    className="form-input"
                                    placeholder="Max"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Nachname</Label>
                                <Input
                                    value={formData.nachname}
                                    onChange={(e) => setFormData({...formData, nachname: e.target.value})}
                                    className="form-input"
                                    placeholder="Mustermann"
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">E-Mail *</Label>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                className="form-input"
                                placeholder="max@beispiel.at"
                            />
                        </div>
                        {!editUser && (
                            <div className="form-group">
                                <Label className="form-label">Passwort *</Label>
                                <Input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                                    className="form-input"
                                    placeholder="••••••••"
                                />
                            </div>
                        )}
                        <div className="form-group">
                            <Label className="form-label">Rolle</Label>
                            <Select
                                value={formData.role}
                                onValueChange={(v) => setFormData({...formData, role: v})}
                            >
                                <SelectTrigger className="form-input">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableRoles.map(role => (
                                        <SelectItem key={role.id || role.value} value={role.id || role.value}>
                                            <div className="flex items-center gap-2">
                                                {ROLE_CONFIG[role.id || role.value]?.icon && (
                                                    <span className={ROLE_CONFIG[role.id || role.value].color}>
                                                        {React.createElement(ROLE_CONFIG[role.id || role.value].icon, { className: "h-4 w-4" })}
                                                    </span>
                                                )}
                                                {role.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {formData.role && ROLE_CONFIG[formData.role] && (
                                <p className="text-xs text-muted-foreground mt-1">
                                    {ROLE_CONFIG[formData.role].description}
                                </p>
                            )}
                        </div>
                        <div className="flex items-center justify-between py-2">
                            <div>
                                <Label className="form-label mb-0">Aktiv</Label>
                                <p className="text-xs text-muted-foreground">
                                    Deaktivierte Benutzer können sich nicht anmelden
                                </p>
                            </div>
                            <Switch
                                checked={formData.aktiv}
                                onCheckedChange={(v) => setFormData({...formData, aktiv: v})}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowUserDialog(false)} className="rounded-xl">
                            Abbrechen
                        </Button>
                        <Button onClick={handleSaveUser} className="rounded-xl">
                            {editUser ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Change Password Dialog */}
            <Dialog open={!!passwordDialog} onOpenChange={() => { setPasswordDialog(null); setNewPassword(''); }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Passwort ändern</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-muted-foreground mb-4">
                            Neues Passwort für <strong>{passwordDialog?.email}</strong>
                        </p>
                        <div className="form-group">
                            <Label className="form-label">Neues Passwort</Label>
                            <Input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="form-input"
                                placeholder="••••••••"
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setPasswordDialog(null); setNewPassword(''); }} className="rounded-xl">
                            Abbrechen
                        </Button>
                        <Button onClick={handleChangePassword} className="rounded-xl">
                            Passwort ändern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie den Benutzer &quot;{deleteConfirm?.email}&quot; wirklich löschen? 
                            Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteUser}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
